import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    errorId: string | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the app
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Generate unique error ID for tracking
        const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
            errorInfo: null,
            errorId,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error details for debugging with structured logging
        const errorDetails = {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            errorId: this.state.errorId,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
        };

        console.error('ErrorBoundary caught an error:', errorDetails);

        // Report to external error tracking service if available
        if (typeof window !== 'undefined' && (window as any).errorTracker) {
            try {
                (window as any).errorTracker.captureException(error, {
                    extra: errorDetails,
                    tags: {
                        component: 'ErrorBoundary',
                        errorId: this.state.errorId,
                    },
                });
            } catch (reportingError) {
                console.error('Failed to report error to tracking service:', reportingError);
            }
        }

        this.setState({
            error,
            errorInfo,
        });

        // Call optional error handler prop
        if (this.props.onError) {
            try {
                this.props.onError(error, errorInfo);
            } catch (handlerError) {
                console.error('Error in onError handler:', handlerError);
            }
        }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
        });
    };

    handleReportIssue = () => {
        const errorDetails = {
            errorId: this.state.errorId,
            message: this.state.error?.message,
            timestamp: new Date().toISOString(),
            url: window.location.href,
        };

        // Create mailto link with error details
        const subject = encodeURIComponent(`Error Report - ${this.state.errorId}`);
        const body = encodeURIComponent(`
Error Details:
- Error ID: ${errorDetails.errorId}
- Message: ${errorDetails.message}
- Time: ${errorDetails.timestamp}
- Page: ${errorDetails.url}

Please describe what you were doing when this error occurred:

`);

        const mailtoLink = `mailto:support@torvia.com?subject=${subject}&body=${body}`;
        window.open(mailtoLink);
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0">
                                <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
                            </div>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600">
                                An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
                            </p>
                            {this.state.errorId && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Error ID: <code className="bg-gray-100 px-1 rounded">{this.state.errorId}</code>
                                </p>
                            )}
                        </div>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mb-4">
                                <summary className="text-sm font-medium text-gray-700 cursor-pointer">Error Details (Development)</summary>
                                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto">
                                    <div className="mb-2">
                                        <strong>Error:</strong> {this.state.error.message}
                                    </div>
                                    <div className="mb-2">
                                        <strong>Stack:</strong>
                                        <pre className="whitespace-pre-wrap">{this.state.error.stack}</pre>
                                    </div>
                                    {this.state.errorInfo && (
                                        <div>
                                            <strong>Component Stack:</strong>
                                            <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                                        </div>
                                    )}
                                </div>
                            </details>
                        )}

                        <div className="flex flex-col space-y-2">
                            <div className="flex space-x-3">
                                <button
                                    onClick={this.handleReset}
                                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Try Again
                                </button>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                >
                                    Refresh Page
                                </button>
                            </div>
                            <button
                                onClick={this.handleReportIssue}
                                className="w-full bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                                Report Issue
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
